import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultTextField from 'components/Utils/DefaultTextField';
import TextAreaAutosize from 'components/Common/TextAreaAutosize';

const errorStyle = {
  color: 'red',
  fontSize: '0.8em',
  marginTop: '4px',
  minHeight: '1.2em',
};

const ChatShortcutDetails = ({ shortcut, onSaveSuccess }) => {
  const [formData, setFormData] = useState({
    id: 'new-shortcut',
    name: '',
    value: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (shortcut && shortcut.id) {
      setFormData({
        id: shortcut.id,
        name: shortcut.name || '',
        value: shortcut.value || '',
      });
    } else {
      setFormData({
        id: 'new-shortcut',
        name: '',
        value: '',
      });
    }
    setErrors({});
    setIsDirty(false);
  }, [shortcut]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setIsDirty(true);
    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    let currentErrors = {};
    if (!formData.name || !formData.name.trim()) {
      currentErrors.name = 'Must type a name';
    }
    if (!formData.value || !formData.value.trim()) {
      currentErrors.value = 'Must type a value';
    }
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const shortcutData = {
      id: formData.id !== 'new-shortcut' ? formData.id : undefined,
      name: formData.name.trim(),
      value: formData.value.trim(),
    };

    setLoading(true);
    setErrors({});
    try {
      const result = await dispatch(api.Chat.addChatShortcut({ chatShortcut: shortcutData }), {
        errorTitle: 'Chat Shortcut',
      });

      if (result && result.id) {
        setFormData((prevData) => ({ ...prevData, id: result.id }));
        setIsDirty(false);
        if (onSaveSuccess) {
          onSaveSuccess(result);
        }
      }
    } catch (error) {
      console.error('Failed to save chat shortcut:', error);
      setErrors({ submit: 'Failed to save shortcut. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled = loading || !isDirty || Object.values(errors).some(Boolean);

  return (
    <div className="chat-shortcuts padded-box">
      <div>
        <DefaultTextField
          name="name"
          label="Name"
          value={formData.name}
          onChange={handleChange}
          style={{ width: '100%' }}
          error={!!errors.name}
          helperText={errors.name || ''}
        />
        <div style={errorStyle}>{errors.name}</div>
      </div>

      <TextAreaAutosize
        name="value"
        label="Value"
        value={formData.value}
        onChange={handleChange}
        style={{ width: '100%' }}
        error={!!errors.value}
        helperText={errors.value || ''}
      />
      <div style={errorStyle}>{errors.value}</div>

      {errors.submit && <div style={{ ...errorStyle, marginTop: '16px' }}>{errors.submit}</div>}

      <DefaultButton
        name="Save Changes"
        style={{ width: '100%', marginTop: '16px' }}
        onClick={handleSubmit}
        loading={loading}
        disabled={isSaveDisabled}
      />
    </div>
  );
};

ChatShortcutDetails.propTypes = {
  shortcut: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    value: PropTypes.string,
  }),
  onSaveSuccess: PropTypes.func,
};

export default ChatShortcutDetails;
